import React, { useState } from 'react';
import { connect } from 'react-redux';
import { SET_ALL_CAMERAS, SET_NVR_TIMEZONE } from '../actions/types';
import { setLocalTimestamp } from '../actions';
import { getBackend } from '../backend';
import './liveStyle.css';

/**
 * CloudDeviceView — the cloud viewer's "jump" control.
 *
 * A dropdown of the user's cloud devices (from the CloudNvrAdapter session).
 * Selecting one switches the ACTIVE DEVICE and repopulates the grid with that
 * device's cameras. Unlike the local NVR jump (ServerView), this is NOT a
 * re-login: one JWT authorizes every device, so a switch is just a device-id
 * change in the stream URLs. JumpControl picks CloudDeviceView vs ServerView by
 * product (productConfig.jumpKind), so LiveView's call sites stay identical.
 *
 * The device id is shown under the dropdown so it's obvious which device is
 * being viewed (validating dragon-vs-legacy while the two are being brought up).
 */
export const CloudDeviceView = (props) => {   // bare export for unit tests (dispatch props passed in)
  const backend = getBackend();
  const targets = backend.getJumpTargets ? backend.getJumpTargets() : [];
  const [activeId, setActiveId] = useState(
    backend.getActiveDeviceId ? backend.getActiveDeviceId() : ''
  );

  const onChange = (e) => {
    const id = e.target.value;
    if (!id || id === activeId) return;
    // One JWT covers every device — switching is a device swap, not a re-login.
    if (backend.setActiveDevice && backend.setActiveDevice(id)) {
      setActiveId(id);
      props.setAllCameras(backend.getGridCameras());
      // Jumping to another site changes the active project, so re-seed the header/rail clock + playback
      // "now" to the NEW site's zone. seedCloudTimezone only runs at LiveView mount, so without this a
      // cross-site jump left the clock (and the dragon playback window) on the old site's zone until a
      // page refresh (#86bbm5p92 review L1).
      if (props.reseedTimezone && backend.getPrimaryTimezone) {
        props.reseedTimezone(backend.getPrimaryTimezone());
        // Recompute nvrTimestampShort in the NEW zone right now, not on the next 1s tick: a playback
        // search fired immediately after the jump reads that value for its lookback window, so without
        // this the first search would use the old site's local time (#86bbm5p92 review L1 follow-up).
        if (props.refreshTimestamp) props.refreshTimestamp();
      }
      if (props.onJumpStarted) props.onJumpStarted();
    }
  };

  const active = targets.find((t) => t.id === activeId);

  return (
    <div className="live-section--server">
      <select value={activeId || ''} onChange={onChange} className="live-select">
        <option value="" disabled>Select device</option>
        {targets.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}{t.siteLabel ? ` — ${t.siteLabel}` : ''}{t.online ? '' : ' [OFFLINE]'}
          </option>
        ))}
      </select>
      {active && (
        <div className="live-status-text" style={{ visibility: 'visible' }} title={active.id}>
          {active.online ? 'Online' : 'Offline'} · {active.id}
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setAllCameras: (cams) => dispatch({ type: SET_ALL_CAMERAS, payload: cams }),
  reseedTimezone: (tz) => dispatch({ type: SET_NVR_TIMEZONE, nvrTimezone: tz || '', nvrDateFormat: '' }),
  refreshTimestamp: () => dispatch(setLocalTimestamp()),   // recompute "now" in the just-seeded zone
});

export default connect(null, mapDispatchToProps)(CloudDeviceView);
